"use client"
import { useState, useEffect, useRef } from 'react'
import { Button, addToast, PopoverTrigger, Popover, PopoverContent, Textarea } from '@heroui/react'
import moment from 'moment'
import EmojiKeyboard from './emoji'
import ImageUploaderModal_V3 from '@/components/ImageUploaderModal_V3'
import ReplyModal from './ReplyModal'
import { useComment } from '@/hooks/useComment'
import useLogin from '@/hooks/useLogin'
import useMobile from '@/hooks/useMobile'
import { track } from '@/lib/log'
import clsx from 'clsx'
import ImagePreview from '@/components/ImagePreview'

const CommentItem = ({
  data,
  doc_id,
  parent_id,
}: {
  data: any
  doc_id: string
  parent_id?: string
  onReply?: (reply: any) => void
}) => {
  const { replyComment, likeComment, dislikeComment, deleteComment, deleteReply, fetchMoreReply } = useComment(doc_id)
  const [replyList, setReplyList] = useState(data?.replies || [])
  const [showMoreReply, setShowMoreReply] = useState(true)
  const { checkLogin } = useLogin();
  useEffect(() => {
    if (replyList.length >= data.reply_n) {
      setShowMoreReply(false)
    }
  }, [replyList, data.reply_n])
  //这里一开始是只显示一条回复，其余回复折叠，点击more后，请求接口并且把折叠的部分展开，然后与本地现有的回复合并，然后更新state
  useEffect(() => {
    if (data?.replies?.length > 0) {
      if (data.replies.some((item: any) => item.comment === '___NO_MORE_REPLY__')) {
        setShowMoreReply(false)
      }
      setReplyList(data.replies.filter((item: any) => item.comment !== '___NO_MORE_REPLY__'))
    } else {
      setReplyList([])
      setShowMoreReply(false)
    }
  }, [data.replies])
  function handleFetchMoreReply(reply: any) {
    console.log("🚀 ~ handleFetchMoreReply ~ reply:", reply)
    fetchMoreReply(data.comment_id, reply.reply_id)
    track("fetch_more_reply", {
      doc_id,
      comment_id: data.comment_id,
      reply_id: reply.reply_id,
      type: "reply",
      action: "fetch_more"
    })
  }
  const [showReplyInput, setShowReplyInput] = useState(false)
  const [showReplyModal, setShowReplyModal] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const textAreaRef = useRef<any>(null)
  const isMobile = useMobile()
  const handleReplyVisible = checkLogin(() => {
    if (isMobile) {
      // 移动端使用底部弹出模态框
      setShowReplyModal(true)
    } else {
      // 桌面端使用内联回复
      if (showReplyInput) {
        setShowReplyInput(false)
      } else {
        setReplyContent('')
        setShowReplyInput(true)
      }
    }
  })
  const handleLike = checkLogin((comment_id: string) => {
    setLikeCount((pre: number) => {
      if (isLiked) {
        return pre - 1
      } else {
        return pre + 1
      }
    })
    setIsLiked((pre: boolean) => !pre)
    likeComment(comment_id).then((res: any) => {
      console.log('🚀 ~ .then ~ res:', res)
    })
    track("like_comment", {
      comment_id: comment_id,
      doc_id,
      action: isLiked ? "unlike" : "like"
    })
    console.log('🚀 ~ handleLike ~ comment_id:', comment_id)
  })
  const handleDislike = checkLogin((comment_id: string) => {
    setDislikeCount((pre: number) => {
      if (isDisliked) {
        return pre - 1
      } else {
        return pre + 1
      }
    })
    setIsDisliked((pre: boolean) => !pre)
    dislikeComment(comment_id).then((res: any) => {
      console.log('🚀 ~ .then ~ res:', res)
    })
  })
  const handleDelete = checkLogin((comment_id: string) => {
    deleteComment(comment_id).then((res: any) => {
      console.log('🚀 ~ .then ~ res:', res)
      addToast({
        title: 'Delete comments successfully',
        color: 'success',
      })
      track("delete_comment", {
        doc_id,
        comment_id,
        type: "comment"
      })
    })
  })
  const handleDeleteReply = checkLogin((parent_id: string, reply_id: string) => {
    deleteReply(parent_id, reply_id).then((res: any) => {
      console.log('🚀 ~ .then ~ res:', res)
      addToast({
        title: 'Delete reply successfully',
        color: 'success',
      })
      // setHidden(true)
      track("delete_comment", {
        doc_id,
        comment_id: reply_id,
        type: "reply"
      })
    })
  })

  const handlePostComment = checkLogin(() => {
    let commentData = replyContent
    if (commentData.trim() === '' && images.length > 0) {
      commentData = '\n'
    }
    const reply = data.reply_id || data.comment_id
    replyComment(reply, commentData, images, parent_id)
      .then((res: any) => {
        console.log('🚀 ~ .then ~ res:', res)
        setShowReplyInput(false)
        track("post_comment", {
          doc_id,
          comment_id: res.comment_id,
          type: "reply"
        })
      })
  })

  // 处理模态框回复提交
  const handleModalReplySubmit = checkLogin((content: string, modalImages?: { url: string; height: number; width: number }[]) => {
    let commentData = content
    if (commentData.trim() === '' && (modalImages?.length || 0) > 0) {
      commentData = '\n'
    }
    const reply = data.reply_id || data.comment_id
    replyComment(reply, commentData, modalImages || [], parent_id)
      .then((res: any) => {
        console.log('🚀 ~ .then ~ res:', res)
        setShowReplyModal(false)
        track("post_comment", {
          doc_id,
          comment_id: res.comment_id,
          type: "reply"
        })
      })
  })
  const [imageUploaderModalVisible, setImageUploaderModalVisible] = useState(false)
  const [images, setImages] = useState<{ url: string; height: number; width: number }[]>([])
  function handleAddImage(urls: string[]) {
    setImageUploaderModalVisible(false)
    urls.slice(0, 1).forEach((url) => {
      const image = new Image()
      image.src = url
      image.onload = () => {
        setImages((pre) => [...pre, { url, height: image.height, width: image.width, video_url: '' }])
      }
    })
  }
  const [isLiked, setIsLiked] = useState(data.upvoted || false)
  const [isDisliked, setIsDisliked] = useState(data.downvoted || false)
  const [likeCount, setLikeCount] = useState(data.like || 0)
  const [dislikeCount, setDislikeCount] = useState(data.dislike || 0)
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false)

  const handleUserClick = () => {
    if (data.media_id) {
      window.open(`/users/${data.media_id}`, '_blank')
    } else if (data.profile_id) {
      window.open(`/users/${data.profile_id}`, '_blank')
    }
  }

  return (
    <>

      <div key={data.reply_id} className="flex items-start gap-2">
        <img
          src={data.profile}
          className={clsx("rounded-full cursor-pointer", parent_id ? "w-8 h-8 min-w-8 min-h-8" : "w-10 h-10 min-w-10 min-h-10")}
          onClick={handleUserClick}
        />

        <div className={clsx(parent_id ? "ml-0" : "ml-0")}>
          <div className="flex items-start gap-2 justify-between pr-2 leading-5">
            <div className="flex items-start gap-2 h-full">
              <span
                title={data.mine ? `${data.nickname}(Me)` : data.nickname}
                className="font-[500] text-gray-500 text-sm max-w-[200px] truncate cursor-pointer hover:text-blue-500"
                onClick={handleUserClick}
              >
                {data.nickname}
                {data.mine && <span className="text-sm text-gray-500"> (Me)</span>}
              </span>
              {data.is_author && (
                <div className="inline-flex items-center bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[10px] font-medium px-2 py-0.5 rounded shadow-sm">
                  <svg className="w-2.5 h-2.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M9.664 1.319a.75.75 0 01.672 0 41.059 41.059 0 018.198 5.424.75.75 0 01-.254 1.285 31.372 31.372 0 00-7.86 3.83.75.75 0 01-.84 0 31.508 31.508 0 00-2.08-1.287V9.394c0-.244.116-.463.302-.592a35.504 35.504 0 013.305-2.033.75.75 0 00-.714-1.319 37 37 0 00-3.446 2.12A2.216 2.216 0 006 9.393v.38a31.293 31.293 0 00-4.28-1.746.75.75 0 01-.254-1.285 41.059 41.059 0 018.198-5.424zM6 11.459a29.848 29.848 0 00-2.455-1.158 41.029 41.029 0 00-.39 3.114.75.75 0 00.419.74c.528.256 1.046.53 1.554.82-.21-.899-.455-1.746-.721-2.517a.75.75 0 00-.407-.999zm2.78 4.42c.138.032.28.07.424.111.59.17 1.203.346 1.821.543.441.14.915.308 1.409.508.21.085.399.22.541.402.278.358.466.734.466 1.107a.75.75 0 01-.537.716 32.905 32.905 0 01-1.928-.777 33.827 33.827 0 00-1.572-.83A29.83 29.83 0 018 15.459v-2.28c.26.333.539.678.78.999z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Author
                </div>
              )}

            </div>
            {data.mine === true &&
              <Popover
                className="text-gray-500 text-xs flex items-center gap-1 cursor-pointer"
              >
                <PopoverTrigger>
                  <div className="rounded-full w-6 h-6 flex items-center justify-center gap-1 cursor-pointer px-0 py-0 bg-transparent hover:bg-gray-100 text-gray-500">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                      />
                    </svg>
                  </div>
                </PopoverTrigger>
                <PopoverContent>
                  <div
                    onClick={() => {
                      if (parent_id) {
                        handleDeleteReply(parent_id, data.reply_id)
                      } else {
                        handleDelete(data.comment_id)
                      }
                    }}
                    className="text-gray-500 text-xs flex items-center gap-1 cursor-pointer hover:bg-gray-100 hover:text-red-500 align-baseline"
                  >
                    Delete Comment
                  </div>
                </PopoverContent>
              </Popover>}
          </div>
          <div className="flex items-center">
            <div className="mb-2">
              {data.reply_to_nickname && (
                <div className="text-sm text-blue-500">@{data.reply_to_nickname}</div>
              )}

              <div className="text-sm mb-1">{data.comment}</div>
              {data?.images?.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  {data.images.map((item: any, index: number) => (
                    <img src={item.url} className="max-h-full max-w-full object-contain rounded-md" key={`${index}_${item.url}`} onClick={() => setImagePreviewVisible(true)}
                      onLoad={(e) => {
                        const img = e.target as HTMLImageElement;
                        console.log('onLoad triggered - naturalWidth:', img.naturalWidth, img.naturalHeight);
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="text-gray-500 text-xs">{time2durationSimple(data.createAt)}</div>
                <div className="text-gray-500 text-xs">{data.location}</div>
              </div>
            </div>

          </div>
          <div className="flex items-center gap-2">
            <div
              className="text-gray-500 text-xs flex items-center gap-1 cursor-pointer mr-1 "
              onClick={() => {
                handleReplyVisible()
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M14.3572 8.00003C14.3572 11.511 11.511 14.3572 8.00003 14.3572C6.85119 14.3572 5.77494 14.0529 4.84586 13.5208C4.65828 13.4134 4.43216 13.3918 4.2699 13.3865C4.08667 13.3805 3.88098 13.3943 3.67576 13.4173C3.2637 13.4634 2.80064 13.5528 2.41804 13.6361C2.413 13.6372 2.40979 13.637 2.40564 13.6356C2.40015 13.6339 2.39189 13.6295 2.38368 13.6213C2.37546 13.613 2.37083 13.6043 2.36883 13.598C2.36724 13.593 2.36696 13.5889 2.36819 13.5833C2.45173 13.2007 2.53958 12.7345 2.57963 12.3098C2.59961 12.0979 2.60896 11.8837 2.59654 11.6889C2.58491 11.5063 2.55145 11.2843 2.44586 11.0952C1.9345 10.1797 1.64288 9.12468 1.64288 8.00003C1.64288 4.48907 4.48907 1.64288 8.00003 1.64288C11.511 1.64288 14.3572 4.48907 14.3572 8.00003Z"
                  stroke="#656565"
                ></path>
                <path d="M5.71429 6.85712H10.2857" stroke="#656565" strokeLinecap="round"></path>
                <path d="M5.71429 9.14288H10.2857" stroke="#656565" strokeLinecap="round"></path>
              </svg>
              Reply{data.reply_n ? `(${data.reply_n})` : ''}
            </div>
            <div
              className={`flex items-center space-x-1 text-xs transition-colors ${isLiked ? "text-red-500" : "text-gray-500 hover:text-red-500 cursor-pointer"
                }`}
              onClick={() => {
                handleLike(data.comment_id || data.reply_id)
              }}
            >
              <svg
                className={`w-5 h-5 ${isLiked ? "fill-red-500" : "fill-none"}`}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <span >
                {likeCount > 0 && `${likeCount}`}
              </span>
            </div>
            <div
              className="text-gray-500 text-xs items-center gap-1 cursor-pointer hidden"
              onClick={() => {
                handleDislike(data.comment_id || data.reply_id)
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0)">
                  <mask
                    id="path-1-outside-1"
                    maskUnits="userSpaceOnUse"
                    x="0.5"
                    y="1.5"
                    width="15"
                    height="14"
                    fill="black"
                  >
                    <rect fill="white" x="0.5" y="1.5" width="15" height="14"></rect>
                    <path d="M4.85938 2.6V6.21563C4.85938 6.65745 4.5012 7.01562 4.05938 7.01562H2.07048C1.85262 7.01562 1.739 7.27489 1.88666 7.43507L7.58516 13.6167C7.68294 13.7228 7.84995 13.7243 7.94965 13.6201L13.8613 7.43841C14.0134 7.27938 13.9007 7.01562 13.6806 7.01562H11.6594C11.2175 7.01562 10.8594 6.65745 10.8594 6.21562V2.6C10.8594 2.54477 10.8146 2.5 10.7594 2.5H4.95937C4.90415 2.5 4.85938 2.54477 4.85938 2.6Z"></path>
                  </mask>
                  <path
                    fill={isDisliked ? 'red' : 'currentColor'}
                    d="M1.88666 7.43507L2.62192 6.75728L2.62192 6.75728L1.88666 7.43507ZM7.58516 13.6167L8.32042 12.9389L8.32041 12.9389L7.58516 13.6167ZM7.94965 13.6201L8.67236 14.3112L8.67236 14.3112L7.94965 13.6201ZM13.8613 7.43841L14.584 8.12956L14.584 8.12956L13.8613 7.43841ZM5.85938 6.21563V2.6H3.85938V6.21563H5.85938ZM4.95937 3.5H10.7594V1.5H4.95937V3.5ZM9.85938 2.6V6.21562H11.8594V2.6H9.85938ZM1.15141 8.11286L6.8499 14.2945L8.32041 12.9389L2.62192 6.75728L1.15141 8.11286ZM8.67236 14.3112L14.584 8.12956L13.1386 6.74726L7.22694 12.9289L8.67236 14.3112ZM13.6806 6.01562H11.6594V8.01562H13.6806V6.01562ZM2.07048 8.01562H4.05938V6.01562H2.07048V8.01562ZM14.584 8.12956C15.3444 7.33441 14.7809 6.01562 13.6806 6.01562V8.01562C13.0205 8.01562 12.6823 7.22436 13.1386 6.74727L14.584 8.12956ZM6.8499 14.2945C7.33879 14.8249 8.17384 14.8325 8.67236 14.3112L7.22694 12.9289C7.52605 12.6161 8.02708 12.6207 8.32042 12.9389L6.8499 14.2945ZM2.62192 6.75728C3.06491 7.23783 2.72405 8.01562 2.07048 8.01562V6.01562C0.98118 6.01562 0.413091 7.31195 1.15141 8.11286L2.62192 6.75728ZM9.85938 6.21562C9.85938 7.20974 10.6653 8.01562 11.6594 8.01562V6.01562C11.7698 6.01562 11.8594 6.10517 11.8594 6.21562H9.85938ZM10.7594 3.5C10.2623 3.5 9.85938 3.09706 9.85938 2.6H11.8594C11.8594 1.99249 11.3669 1.5 10.7594 1.5V3.5ZM5.85938 2.6C5.85938 3.09705 5.45643 3.5 4.95937 3.5V1.5C4.35186 1.5 3.85938 1.99249 3.85938 2.6H5.85938ZM3.85938 6.21563C3.85938 6.10517 3.94892 6.01562 4.05938 6.01562V8.01562C5.05349 8.01562 5.85938 7.20974 5.85938 6.21563H3.85938Z"
                    mask="url(#path-1-outside-1)"
                  ></path>
                </g>
                <defs>
                  <clipPath id="clip0">
                    <rect width="16" height="16" fill="white"></rect>
                  </clipPath>
                </defs>
              </svg>
              <span>
                {dislikeCount && `${dislikeCount}`}
              </span>
            </div>
          </div>
          {/* 桌面端内联回复输入框 */}
          {!isMobile && showReplyInput && (
            <div className="mt-2">
              <div className="relative">
                <Textarea
                  ref={textAreaRef}
                  value={replyContent}
                  onValueChange={(e) => setReplyContent(e)}
                  placeholder="Write your reply..."
                  rows={3}
                  autoFocus
                  className="mb-2 pr-12 selectable"
                  style={{
                    fontSize: '16px', // 防止移动端浏览器自动放大
                    lineHeight: '1.5'
                  }}
                />
              </div>
              <div className="flex justify-between">
                <div className="flex items-between gap-2 mb-2">
                  <EmojiKeyboard
                    onEmojiSelect={(emoji: string) => {
                      const textarea = textAreaRef.current?.resizableTextArea?.textArea
                      if (textarea) {
                        const start = textarea.selectionStart
                        const end = textarea.selectionEnd
                        const newContent =
                          replyContent.substring(0, start) + emoji + replyContent.substring(end)
                        setReplyContent(newContent)

                        // 保持光标位置在emoji之后
                        setTimeout(() => {
                          textarea.selectionStart = textarea.selectionEnd = start + emoji.length
                          textarea.focus()
                        }, 0)
                      } else {
                        // 如果无法获取光标位置，就添加到末尾
                        setReplyContent(replyContent + emoji)
                      }
                    }}
                  />
                </div>
                <Button
                  className='text-white bg-black'
                  onPress={() => {
                    handlePostComment()
                  }}
                  disabled={(!replyContent || replyContent.trim() === '') && images.length === 0}
                >
                  Post
                </Button>
              </div>
            </div>
          )}
          {replyList.length > 0 && (
            <>
              <div className="flex flex-col gap-2 ml-[0px] my-3">
                {replyList.map((item: any) => (
                  <CommentItem
                    key={item.reply_id}
                    data={item}
                    doc_id={doc_id}
                    parent_id={data.comment_id}
                  />
                ))}
                {showMoreReply && (
                  <div
                    className="text-blue-500 text-xs flex items-center gap-1 cursor-pointer"
                    onClick={() => {
                      handleFetchMoreReply(replyList[replyList.length - 1])
                    }}
                  >
                    <span>More replies</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <ImageUploaderModal_V3
        visible={imageUploaderModalVisible}
        onClose={() => setImageUploaderModalVisible(false)}
        onOk={(urls: string[]) => {
          handleAddImage(urls)
        }}
        initialImages={images.map((item) => item.url)}
      />

      {/* 移动端底部弹出回复模态框 */}
      <ReplyModal
        isOpen={showReplyModal}
        onClose={() => setShowReplyModal(false)}
        onSubmit={handleModalReplySubmit}
        replyToUser={{
          nickname: data.nickname,
          profile: data.profile
        }}
        isMobile={isMobile}
      />
      <ImagePreview
        src={data.images?.[0]?.url || ''}
        isOpen={imagePreviewVisible}
        onOpenChange={() => setImagePreviewVisible(!imagePreviewVisible)}
        isMobile={isMobile}
      />
    </>
  )
}

function time2durationSimple(datetime: string) {
  if (!datetime) {
    return 'now'
  }
  const now = moment()
  const time = moment.utc(datetime)
  const d = moment.duration(now.diff(time))
  let timeStr = ''
  if (d.years() > 0) {
    timeStr = time.format('YYYY-MM-DD')
  } else if (d.months() > 0) {
    timeStr = time.format('MM-DD')
  } else if (d.days() > 0) {
    timeStr = `${d.days()}d`
  } else if (d.hours() > 0) {
    timeStr = `${d.hours()}h`
  } else if (d.minutes() > 0) {
    timeStr = `${d.minutes()}m`
  } else {
    timeStr = `now`
  }
  return timeStr
}

export default CommentItem